"use client";

import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { DEFAULT_LOCALE_KEY } from "@faire/web/common/consts/DEFAULT_LOCALE";
import { useShowDeferredContent } from "@faire/web/common/deferredRender";
import { buildLocale } from "@faire/web/common/localization/utils";
import { logError } from "@faire/web/common/logging";
import { getLocaleKey } from "@faire/web/common/serialized-data/getLocaleKey";
import { QueryParameters as NavigationCardQueryParams } from "@faire/web-api--source/endpoints/www/api/navigation/get";
import { useQuery as useNavigationCardDataQuery } from "@faire/web-api--source/endpoints/www/api/navigation/get-hooks";
import { useQuery as useNavigationCardHashcodeQuery } from "@faire/web-api--source/endpoints/www/api/navigation/hashcode/get-hooks";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import React, { useEffect, useMemo } from "react";

export const useNavigationCardQuery = (
  clientPlatformType: ClientPlatformType
) => {
  const retailerToken = getRetailer()?.token;
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const localeKey = getLocaleKey();
  const locale = buildLocale(localeKey ?? DEFAULT_LOCALE_KEY);
  const country = getSessionCountry();

  const {
    data: navigationCardHashCodeData,
    isError: isNavigationCardHashcodeError,
    error: navigationCardHashcodeError,
    refetch: refetchHashcode,
  } = useNavigationCardHashcodeQuery(undefined, {
    enabled: isLoggedInRetailer,
  });

  const navigationCardQuery = useMemo(() => {
    return NavigationCardQueryParams.build({
      retailerToken,
      locale,
      clientPlatformType: clientPlatformType,
      country,
      hashCode: navigationCardHashCodeData?.navigation_hashcode,
    });
  }, [
    clientPlatformType,
    country,
    locale,
    navigationCardHashCodeData?.navigation_hashcode,
    retailerToken,
  ]);

  const {
    data: navigationCardData,
    isError: isNavigationCardError,
    error: navigationCardError,
  } = useNavigationCardDataQuery(navigationCardQuery, {
    enabled: !!navigationCardHashCodeData?.navigation_hashcode,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isNavigationCardError) {
      logError(navigationCardError);
    }
  }, [isNavigationCardError, navigationCardError]);

  useEffect(() => {
    if (isNavigationCardHashcodeError) {
      logError(navigationCardHashcodeError);
    }
  }, [isNavigationCardHashcodeError, navigationCardHashcodeError]);

  return {
    navigationCardData,
    refetchHashcode,
  };
};

type Props = {
  clientPlatformType: ClientPlatformType;
};

const MountNavigationCardQuery: React.FC<Props> = ({ clientPlatformType }) => {
  useNavigationCardQuery(clientPlatformType);
  return null;
};

export const DeferredMountNavigationCardQuery: React.FC<Props> = ({
  clientPlatformType,
}) => {
  const showDeferredContent = useShowDeferredContent();
  if (!showDeferredContent) {
    return null;
  }
  return <MountNavigationCardQuery clientPlatformType={clientPlatformType} />;
};
